import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { currentUserAtom, authLoadingAtom } from '@/hooks/atoms';
import { onAuthChange, consumeRedirectResult, signInWithWbd } from '@/lib/authService';

interface AuthGateProps {
  children: React.ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const [user, setUser] = useAtom(currentUserAtom);
  const [loading, setLoading] = useAtom(authLoadingAtom);

  useEffect(() => {
    consumeRedirectResult().catch((err) => {
      console.error('Sign-in redirect failed:', err);
    });

    const unsubscribe = onAuthChange((nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });

    return unsubscribe;
  }, [setUser, setLoading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  return <>{children}</>;
}

function LoginScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 max-w-sm px-4">
        <h1 className="text-xl font-semibold text-foreground">AI Forensics</h1>
        <p className="text-sm text-muted-foreground">Sign in with your WBD account to continue.</p>
        <Button onClick={() => signInWithWbd()}>Sign in with WBD</Button>
      </div>
    </div>
  );
}
